import { ActionIcon, Button, Loader, Text, Tooltip } from '@mantine/core';
import { IconCamera, IconPhotoPlus, IconRefresh, IconX } from '@tabler/icons-react';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { INVENTORY_PHOTOS_MAX } from 'src/Components/Inventory/inventoryLabels';
import { communityFeedApi } from 'src/Services/API';

import styles from './InventoryPhotoPicker.module.scss';
import { Props } from './InventoryPhotoPicker.types';

/**
 * 1 to 5 photos. On a phone the button opens the camera directly; each photo is sent to the
 * server as soon as it is taken, so the next step does not wait.
 */
const InventoryPhotoPicker = ({ photos, onChange, large }: Props) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const uploading = useRef(new Set<string>());
  const remaining = INVENTORY_PHOTOS_MAX - photos.length;

  // Local previews are created once per file, and released when the photo goes away.
  const localUrls = useMemo(
    () =>
      new Map(
        photos
          .filter(photo => photo.nativeFile && !photo.serverFile)
          .map(photo => [photo.key, URL.createObjectURL(photo.nativeFile!)])
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [photos.map(photo => `${photo.key}:${!!photo.serverFile}`).join('|')]
  );
  useEffect(() => () => localUrls.forEach(url => URL.revokeObjectURL(url)), [localUrls]);

  useEffect(() => {
    photos
      .filter(photo => photo.nativeFile && !photo.serverFile && !photo.failed)
      .filter(photo => !uploading.current.has(photo.key))
      .forEach(photo => {
        uploading.current.add(photo.key);
        communityFeedApi
          .communityFeedControllerCreateFeedItemFile(photo.nativeFile!)
          .then(({ data }) =>
            onChange(current =>
              current.map(p => (p.key === photo.key ? { ...p, serverFile: data } : p))
            )
          )
          .catch(() =>
            onChange(current =>
              current.map(p => (p.key === photo.key ? { ...p, failed: true } : p))
            )
          )
          .finally(() => uploading.current.delete(photo.key));
      });
  }, [photos, onChange]);

  const addFiles = (files: FileList | null) => {
    if (!files?.length) return;
    const added = Array.from(files)
      .slice(0, remaining)
      .map(file => ({ key: `${Date.now()}-${file.name}-${Math.random()}`, nativeFile: file }));
    onChange(current => [...current, ...added]);
  };

  const retry = (key: string) =>
    onChange(current => current.map(p => (p.key === key ? { ...p, failed: false } : p)));

  return (
    <div className={styles.container}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        hidden
        onChange={event => {
          addFiles(event.currentTarget.files);
          event.currentTarget.value = '';
        }}
      />

      {photos.length > 0 && (
        <div className={styles.grid}>
          {photos.map((photo, index) => (
            <div key={photo.key} className={styles.photo}>
              <img
                src={
                  photo.serverFile?.previewMediumUrl ??
                  photo.serverFile?.url ??
                  localUrls.get(photo.key)
                }
                alt={t('inventory.photos.alt', 'Photo {{n}}', { n: index + 1 })}
              />
              {!photo.serverFile && !photo.failed && (
                <div className={styles.overlay}>
                  <Loader size="sm" color="white" />
                </div>
              )}
              {photo.failed && (
                <div className={styles.overlay}>
                  <Button
                    size="xs"
                    variant="white"
                    leftSection={<IconRefresh size={14} />}
                    onClick={() => retry(photo.key)}
                  >
                    {t('inventory.photos.retry', 'Réessayer')}
                  </Button>
                </div>
              )}
              <Tooltip label={t('inventory.photos.remove', 'Retirer la photo')}>
                <ActionIcon
                  className={styles.remove}
                  variant="white"
                  color="dark"
                  radius="xl"
                  size="lg"
                  aria-label={t('inventory.photos.remove', 'Retirer la photo')}
                  onClick={() => onChange(current => current.filter(p => p.key !== photo.key))}
                >
                  <IconX size={18} />
                </ActionIcon>
              </Tooltip>
            </div>
          ))}
        </div>
      )}

      {remaining > 0 ? (
        <Button
          size={large ? 'xl' : 'md'}
          variant={large && photos.length === 0 ? 'filled' : 'outline'}
          fullWidth={large}
          leftSection={photos.length === 0 ? <IconCamera size={24} /> : <IconPhotoPlus size={22} />}
          onClick={() => inputRef.current?.click()}
        >
          {photos.length === 0
            ? t('inventory.photos.take', 'Prendre une photo')
            : t('inventory.photos.another', 'Ajouter une photo ({{count}} sur {{max}})', {
                count: photos.length,
                max: INVENTORY_PHOTOS_MAX,
              })}
        </Button>
      ) : (
        <Text size="sm" c="dimmed">
          {t('inventory.photos.max', '5 photos au plus.')}
        </Text>
      )}
    </div>
  );
};

export default InventoryPhotoPicker;
